/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  getDay,
  isBefore,
  isSameDay,
  isToday,
  startOfDay,
  startOfMonth,
  subMonths,
} from "date-fns";
import Breadcrumb from "../../components/Breadcrumb";
import makeRequest from "../../makeRequest";
import { PatientAuthContext } from "../../contexts/PatientAuthContext";

/* ─── Injected styles ─────────────────────────────────────────────── */
const STYLE_ID = "my-appts-styles";
if (typeof document !== "undefined" && !document.getElementById(STYLE_ID)) {
  const s = document.createElement("style");
  s.id = STYLE_ID;
  s.textContent = `
    @keyframes maSpin { to { transform: rotate(360deg); } }
    .ma-spinner {
      width: 44px; height: 44px; border: 4px solid #e6e9f0;
      border-top-color: #0f6fbf; border-radius: 50%;
      animation: maSpin .8s linear infinite; margin: 60px auto; display: block;
    }
    .ma-card {
      background: #fff; border-radius: 14px; border: 1px solid #e6e9f0;
      padding: 24px 26px; margin-bottom: 20px;
      box-shadow: 0 2px 12px rgba(15,111,191,.06);
      transition: box-shadow .2s;
    }
    .ma-card:hover { box-shadow: 0 4px 24px rgba(15,111,191,.13); }
    .ma-badge {
      display: inline-block; padding: 3px 12px; border-radius: 50px;
      font-size: .75rem; font-weight: 700; letter-spacing: .03em; text-transform: uppercase;
    }
    .ma-badge-upcoming { background: #e8f5e9; color: #2e7d32; }
    .ma-badge-past     { background: #f3f4f6; color: #888; }
    .ma-doctor-row { display: flex; align-items: center; gap: 14px; margin-bottom: 16px; }
    .ma-doctor-icon {
      width: 52px; height: 52px; border-radius: 50%; background: #e8f3fb;
      display: flex; align-items: center; justify-content: center;
      font-size: 1.4rem; color: #0f6fbf; flex-shrink: 0;
    }
    .ma-doctor-name { font-size: 1.05rem; font-weight: 700; color: #1a2744; margin: 0 0 2px; }
    .ma-speciality   { font-size: .82rem; color: #6b7280; margin: 0; }
    .ma-meta-row { display: flex; flex-wrap: wrap; gap: 18px; margin-bottom: 18px; }
    .ma-meta-item { display: flex; align-items: center; gap: 7px; font-size: .875rem; color: #4b5563; }
    .ma-meta-item i { color: #0f6fbf; font-size: .9rem; width: 16px; text-align: center; }
    .ma-actions { display: flex; gap: 10px; flex-wrap: wrap; }
    .ma-btn {
      padding: 8px 20px; border-radius: 8px; font-size: .875rem; font-weight: 600;
      cursor: pointer; border: none; transition: all .2s;
    }
    .ma-btn-cancel  { background: #fee2e2; color: #b91c1c; }
    .ma-btn-cancel:hover  { background: #fca5a5; }
    .ma-btn-reschedule { background: #dbeafe; color: #1d4ed8; }
    .ma-btn-reschedule:hover { background: #bfdbfe; }
    .ma-btn-disabled { opacity: .45; cursor: not-allowed; }
    /* Pagination */
    .ma-pagination { display: flex; justify-content: center; align-items: center; gap: 6px; margin-top: 32px; flex-wrap: wrap; }
    .ma-page-btn {
      min-width: 36px; height: 36px; padding: 0 10px; border-radius: 8px;
      border: 1px solid #d1d5db; background: #fff; color: #374151;
      cursor: pointer; font-size: .875rem; transition: all .2s; display: flex; align-items: center; justify-content: center;
    }
    .ma-page-btn:hover { background: #e8f3fb; border-color: #0f6fbf; color: #0f6fbf; }
    .ma-page-btn.active { background: #0f6fbf; border-color: #0f6fbf; color: #fff; }
    .ma-page-btn:disabled { opacity: .4; cursor: not-allowed; }
    /* Modal overlay */
    .ma-overlay {
      position: fixed; inset: 0; background: rgba(15,23,42,.55);
      z-index: 9000; display: flex; align-items: center; justify-content: center;
      padding: 16px;
    }
    .ma-modal {
      background: #fff; border-radius: 18px; width: 100%; max-width: 500px;
      max-height: 90vh; overflow-y: auto; padding: 32px;
      box-shadow: 0 20px 60px rgba(0,0,0,.25);
    }
    .ma-modal-title { font-size: 1.25rem; font-weight: 700; color: #1a2744; margin-bottom: 6px; }
    .ma-modal-sub   { font-size: .9rem; color: #6b7280; margin-bottom: 24px; }
    .ma-modal-footer { display: flex; gap: 10px; justify-content: flex-end; margin-top: 24px; }
    .ma-btn-primary { background: #0f6fbf; color: #fff; padding: 10px 24px; border-radius: 8px; border: none; font-weight: 600; cursor: pointer; font-size: .9rem; }
    .ma-btn-primary:hover { background: #0a5799; }
    .ma-btn-secondary { background: #f3f4f6; color: #374151; padding: 10px 24px; border-radius: 8px; border: none; font-weight: 600; cursor: pointer; font-size: .9rem; }
    .ma-btn-secondary:hover { background: #e5e7eb; }
    /* Calendar */
    .ma-cal-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
    .ma-cal-title   { font-weight: 700; font-size: 1rem; color: #1a2744; }
    .ma-cal-nav { background: none; border: 1px solid #d1d5db; border-radius: 8px; width: 34px; height: 34px; cursor: pointer; color: #374151; display: flex; align-items: center; justify-content: center; }
    .ma-cal-nav:hover { background: #e8f3fb; border-color: #0f6fbf; color: #0f6fbf; }
    .ma-cal-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 3px; }
    .ma-cal-day-label { text-align: center; font-size: .7rem; font-weight: 700; color: #9ca3af; padding: 4px 0; text-transform: uppercase; }
    .ma-cal-day {
      aspect-ratio: 1; border-radius: 8px; border: none; background: transparent;
      font-size: .82rem; cursor: pointer; transition: all .15s; display: flex; align-items: center; justify-content: center;
    }
    .ma-cal-day:hover:not(:disabled) { background: #dbeafe; color: #1d4ed8; }
    .ma-cal-day.today { font-weight: 700; color: #0f6fbf; }
    .ma-cal-day.selected { background: #0f6fbf !important; color: #fff !important; font-weight: 700; }
    .ma-cal-day:disabled { color: #d1d5db; cursor: not-allowed; }
    /* Slots */
    .ma-slots-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-top: 16px; }
    .ma-slot-btn {
      padding: 9px 6px; border-radius: 8px; border: 1.5px solid #d1d5db;
      font-size: .82rem; font-weight: 600; cursor: pointer; background: #fff; color: #374151;
      transition: all .15s; text-align: center;
    }
    .ma-slot-btn:hover { border-color: #0f6fbf; color: #0f6fbf; background: #eff6ff; }
    .ma-slot-btn.selected { background: #0f6fbf; border-color: #0f6fbf; color: #fff; }
    .ma-empty { text-align: center; padding: 64px 24px; }
    .ma-empty-icon { font-size: 3rem; color: #d1d5db; margin-bottom: 16px; }
    .ma-empty-title { font-size: 1.15rem; font-weight: 700; color: #374151; margin-bottom: 8px; }
    .ma-empty-sub   { font-size: .9rem; color: #9ca3af; }
  `;
  document.head.appendChild(s);
}

/* ─── Helpers ──────────────────────────────────────────────────────── */
const isUpcoming = (appt) => {
  const d = new Date(appt.APPOINTMENT);
  return !isNaN(d) && d >= startOfDay(new Date());
};

const parseSlotTime = (timeSlot) => {
  try {
    const parts = String(timeSlot || "")
      .trim()
      .split(" ");
    if (parts.length < 2) return timeSlot;
    const timeParts = parts[1].split(":");
    let h = parseInt(timeParts[0], 10);
    const m = parseInt(timeParts[1], 10);
    const ampm = h >= 12 ? "PM" : "AM";
    if (h > 12) h -= 12;
    if (h === 0) h = 12;
    return `${h}:${String(m).padStart(2, "0")} ${ampm}`;
  } catch {
    return timeSlot;
  }
};

const formatApptDate = (raw) => {
  try {
    const d = new Date(raw);
    if (isNaN(d)) return raw;
    return format(d, "EEE, MMM d, yyyy");
  } catch {
    return raw;
  }
};

const formatApptTime = (raw) => {
  try {
    const d = new Date(raw);
    if (isNaN(d)) return "";
    return format(d, "h:mm a");
  } catch {
    return "";
  }
};

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/* ─── Calendar component (reused in reschedule modal) ─────────────── */
function RescheduleCalendar({ currentMonth, selectedDate, onDayClick, onPrev, onNext }) {
  const today = startOfDay(new Date());
  const start = startOfMonth(currentMonth);
  const end = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start, end });
  const prefixBlanks = getDay(start);

  return (
    <div>
      <div className="ma-cal-header">
        <button className="ma-cal-nav" onClick={onPrev}>
          <i className="fa-regular fa-chevron-left" style={{ fontSize: "0.75rem" }}></i>
        </button>
        <span className="ma-cal-title">{format(currentMonth, "MMMM yyyy")}</span>
        <button className="ma-cal-nav" onClick={onNext}>
          <i className="fa-regular fa-chevron-right" style={{ fontSize: "0.75rem" }}></i>
        </button>
      </div>
      <div className="ma-cal-grid">
        {DAY_LABELS.map((d) => (
          <div key={d} className="ma-cal-day-label">
            {d}
          </div>
        ))}
        {Array.from({ length: prefixBlanks }).map((_, i) => (
          <div key={`b-${i}`} />
        ))}
        {days.map((day) => {
          const past = isBefore(day, today);
          const selected = selectedDate && isSameDay(day, selectedDate);
          const todayFlag = isToday(day);
          return (
            <button
              key={day.toISOString()}
              className={`ma-cal-day${todayFlag ? " today" : ""}${selected ? " selected" : ""}`}
              disabled={past}
              onClick={() => onDayClick(day)}
            >
              {format(day, "d")}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Cancel confirmation modal ────────────────────────────────────── */
function CancelModal({ appointment, onClose, onConfirm, loading }) {
  return (
    <div className="ma-overlay" onClick={onClose}>
      <div className="ma-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ma-modal-title">Cancel Appointment</div>
        <p className="ma-modal-sub">
          Are you sure you want to cancel your appointment with <strong>{appointment.DOCTOR_NAME || "the doctor"}</strong> on{" "}
          <strong>{formatApptDate(appointment.APPOINTMENT)}</strong>?
          <br />
          <span style={{ color: "#ef4444", fontSize: ".82rem" }}>This action cannot be undone.</span>
        </p>
        <div className="ma-modal-footer">
          <button className="ma-btn-secondary" onClick={onClose} disabled={loading}>
            Keep Appointment
          </button>
          <button className="ma-btn-primary" style={{ background: "#dc2626" }} onClick={onConfirm} disabled={loading}>
            {loading ? "Cancelling…" : "Yes, Cancel"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Reschedule modal ─────────────────────────────────────────────── */
function RescheduleModal({ appointment, patient, onClose, onSuccess }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchSlots = useCallback(
    async (date) => {
      setSlotsLoading(true);
      setSlots([]);
      setSelectedSlot(null);
      try {
        const startDate = encodeURIComponent(format(date, "yyyy-MM-dd") + " 00:00:00");
        const endDate = encodeURIComponent(format(date, "yyyy-MM-dd") + " 23:59:59");
        const data = await makeRequest({
          route: `/taleeb/slots?doctorId=${appointment.DOCTOR_ID}&startDate=${startDate}&endDate=${endDate}`,
          method: "get",
        });
        setSlots(data?.data?.slots?.slots || []);
      } catch (err) {
        toast.error("Could not load slots. Please try another date.");
      } finally {
        setSlotsLoading(false);
      }
    },
    [appointment.DOCTOR_ID],
  );

  const handleDayClick = (day) => {
    setSelectedDate(day);
    fetchSlots(day);
  };

  const handleConfirm = async () => {
    if (!selectedSlot) return;
    setSaving(true);
    try {
      await makeRequest({
        route: "/taleeb/reschedule-appointment",
        method: "put",
        data: {
          mrno: patient.mrno,
          srNo: selectedSlot.SR_NO,
          oldSrNo: appointment.SR_NO,
        },
      });
      toast.success("Appointment rescheduled successfully!");
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not reschedule. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ma-overlay" onClick={onClose}>
      <div className="ma-modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <div className="ma-modal-title">Reschedule Appointment</div>
        <p className="ma-modal-sub">
          Select a new date &amp; time slot for your appointment with <strong>{appointment.DOCTOR_NAME || "the doctor"}</strong>.
        </p>

        <RescheduleCalendar
          currentMonth={currentMonth}
          selectedDate={selectedDate}
          onDayClick={handleDayClick}
          onPrev={() => setCurrentMonth((m) => subMonths(m, 1))}
          onNext={() => setCurrentMonth((m) => addMonths(m, 1))}
        />

        {selectedDate && (
          <div style={{ marginTop: 20 }}>
            <div style={{ fontWeight: 700, color: "#1a2744", marginBottom: 8, fontSize: ".9rem" }}>
              Available slots — {format(selectedDate, "EEE, MMM d, yyyy")}
            </div>
            {slotsLoading ? (
              <div style={{ textAlign: "center", padding: "20px 0" }}>
                <div className="ma-spinner" style={{ width: 28, height: 28, margin: "0 auto" }}></div>
              </div>
            ) : slots.length === 0 ? (
              <div style={{ color: "#9ca3af", fontSize: ".875rem", padding: "12px 0" }}>No slots available for this date.</div>
            ) : (
              <div className="ma-slots-grid">
                {slots.map((slot) => (
                  <button
                    key={slot.SR_NO}
                    className={`ma-slot-btn${selectedSlot?.SR_NO === slot.SR_NO ? " selected" : ""}`}
                    onClick={() => setSelectedSlot(slot)}
                  >
                    {parseSlotTime(slot.TIME_SLOT)}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="ma-modal-footer">
          <button className="ma-btn-secondary" onClick={onClose} disabled={saving}>
            Back
          </button>
          <button className="ma-btn-primary" onClick={handleConfirm} disabled={!selectedSlot || saving}>
            {saving ? "Rescheduling…" : "Confirm Reschedule"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Appointment card ─────────────────────────────────────────────── */
function AppointmentCard({ appt, onCancel, onReschedule }) {
  const upcoming = isUpcoming(appt);
  return (
    <div className="ma-card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
        <div className="ma-doctor-row" style={{ margin: 0 }}>
          <div className="ma-doctor-icon">
            <i className="fa-regular fa-user-doctor"></i>
          </div>
          <div>
            <p className="ma-doctor-name">{appt.DOCTOR_NAME || "Doctor"}</p>
            <p className="ma-speciality">{appt.SPECIALITY || ""}</p>
          </div>
        </div>
        <span className={`ma-badge ${upcoming ? "ma-badge-upcoming" : "ma-badge-past"}`}>{upcoming ? "Upcoming" : "Past"}</span>
      </div>

      <div className="ma-meta-row">
        <div className="ma-meta-item">
          <i className="fa-regular fa-calendar"></i>
          <span>{formatApptDate(appt.APPOINTMENT)}</span>
        </div>
        <div className="ma-meta-item">
          <i className="fa-regular fa-clock"></i>
          <span>{formatApptTime(appt.APPOINTMENT)}</span>
        </div>
        {appt.CLINIC && (
          <div className="ma-meta-item">
            <i className="fa-regular fa-hospital"></i>
            <span>{appt.CLINIC}</span>
          </div>
        )}
        {appt.SR_NO && (
          <div className="ma-meta-item">
            <i className="fa-regular fa-hashtag"></i>
            <span>SR# {appt.SR_NO}</span>
          </div>
        )}
      </div>

      {upcoming && (
        <div className="ma-actions">
          <button className="ma-btn ma-btn-reschedule" onClick={() => onReschedule(appt)}>
            <i className="fa-regular fa-calendar-pen" style={{ marginRight: 6 }}></i>
            Reschedule
          </button>
          <button className="ma-btn ma-btn-cancel" onClick={() => onCancel(appt)}>
            <i className="fa-regular fa-xmark" style={{ marginRight: 6 }}></i>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── Pagination ────────────────────────────────────────────────────── */
function Pagination({ page, totalPages, onPage }) {
  if (totalPages <= 1) return null;
  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || Math.abs(i - page) <= 1) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== "…") {
      pages.push("…");
    }
  }
  return (
    <div className="ma-pagination">
      <button className="ma-page-btn" disabled={page === 1} onClick={() => onPage(page - 1)}>
        <i className="fa-regular fa-chevron-left" style={{ fontSize: "0.7rem" }}></i>
      </button>
      {pages.map((p, i) =>
        p === "…" ? (
          <span key={`e-${i}`} style={{ padding: "0 4px", color: "#9ca3af" }}>
            …
          </span>
        ) : (
          <button key={p} className={`ma-page-btn${p === page ? " active" : ""}`} onClick={() => onPage(p)}>
            {p}
          </button>
        ),
      )}
      <button className="ma-page-btn" disabled={page === totalPages} onClick={() => onPage(page + 1)}>
        <i className="fa-regular fa-chevron-right" style={{ fontSize: "0.7rem" }}></i>
      </button>
    </div>
  );
}

/* ─── Main component ────────────────────────────────────────────────── */
const LIMIT = 8;

export default function MyAppointmentsMain() {
  const navigate = useNavigate();
  const { patient, isLoading: authLoading } = useContext(PatientAuthContext);

  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState("all"); // "all" | "upcoming" | "past"

  const [cancelModal, setCancelModal] = useState(null); // appointment | null
  const [cancelLoading, setCancelLoading] = useState(false);
  const [rescheduleModal, setRescheduleModal] = useState(null); // appointment | null

  /* Auth guard */
  useEffect(() => {
    if (!authLoading && !patient) {
      navigate("/patient-login?redirect=/my-appointments");
    }
  }, [patient, authLoading, navigate]);

  /* Fetch appointments */
  const fetchAppointments = useCallback(
    async (p = 1) => {
      if (!patient) return;
      setLoading(true);
      try {
        const data = await makeRequest({
          route: `/taleeb/appointments?mrno=${encodeURIComponent(patient.mrno)}&page=${p}&limit=${LIMIT}`,
          method: "get",
        });
        const appts = data?.data?.appointments?.data || [];
        setAppointments(appts);
        const total = data?.data?.appointments?.total_items || 0;
        setTotalPages(Math.ceil(total / LIMIT) || 1);
      } catch (err) {
        toast.error("Could not load appointments. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [patient],
  );

  useEffect(() => {
    if (patient) fetchAppointments(page);
  }, [patient, page, fetchAppointments]);

  /* Cancel */
  const handleCancelConfirm = async () => {
    if (!cancelModal) return;
    setCancelLoading(true);
    try {
      await makeRequest({
        route: "/taleeb/cancel-appointment",
        method: "put",
        data: { mrno: patient.mrno, srNo: cancelModal.SR_NO },
      });
      toast.success("Appointment cancelled.");
      setCancelModal(null);
      fetchAppointments(page);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not cancel. Please try again.");
    } finally {
      setCancelLoading(false);
    }
  };

  const handleRescheduleSuccess = () => {
    setRescheduleModal(null);
    fetchAppointments(page);
  };

  /* Filtered view */
  const filtered = appointments.filter((a) => {
    if (filter === "upcoming") return isUpcoming(a);
    if (filter === "past") return !isUpcoming(a);
    return true;
  });

  if (authLoading) {
    return (
      <main>
        <Breadcrumb title="My Appointments" />
        <div className="container pt-120 pb-120">
          <div className="ma-spinner"></div>
        </div>
      </main>
    );
  }

  if (!patient) return null;

  return (
    <main>
      <Breadcrumb title="My Appointments" />

      <div className="container pt-80 pb-120">
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 32 }}>
          <div>
            <h2 style={{ fontSize: "1.6rem", fontWeight: 800, color: "#1a2744", margin: 0 }}>My Appointments</h2>
            <p style={{ color: "#6b7280", fontSize: ".9rem", margin: "4px 0 0" }}>
              {patient.mrno} — {patient.name || [patient.firstName, patient.lastName].filter(Boolean).join(" ")}
            </p>
          </div>
          <button
            className="ma-btn-primary"
            style={{ borderRadius: 8, fontSize: ".875rem", padding: "10px 20px", cursor: "pointer" }}
            onClick={() => navigate("/appointment")}
          >
            <i className="fa-regular fa-calendar-plus" style={{ marginRight: 7 }}></i>
            Book New Appointment
          </button>
        </div>

        {/* Filter tabs */}
        <div style={{ display: "flex", gap: 6, marginBottom: 28 }}>
          {[
            { key: "all", label: "All" },
            { key: "upcoming", label: "Upcoming" },
            { key: "past", label: "Past" },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              style={{
                padding: "7px 18px",
                borderRadius: 8,
                border: "1.5px solid",
                borderColor: filter === key ? "#0f6fbf" : "#d1d5db",
                background: filter === key ? "#0f6fbf" : "#fff",
                color: filter === key ? "#fff" : "#374151",
                fontWeight: 600,
                fontSize: ".875rem",
                cursor: "pointer",
                transition: "all .2s",
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="ma-spinner"></div>
        ) : filtered.length === 0 ? (
          <div className="ma-empty">
            <div className="ma-empty-icon">
              <i className="fa-regular fa-calendar-xmark"></i>
            </div>
            <div className="ma-empty-title">
              {filter === "upcoming" ? "No upcoming appointments" : filter === "past" ? "No past appointments" : "No appointments found"}
            </div>
            <p className="ma-empty-sub">
              {filter === "all" ? "You haven't booked any appointments yet." : `You have no ${filter} appointments.`}
            </p>
            {filter !== "past" && (
              <button className="ma-btn-primary" style={{ marginTop: 20, cursor: "pointer" }} onClick={() => navigate("/appointment")}>
                Book an Appointment
              </button>
            )}
          </div>
        ) : (
          <>
            {filtered.map((appt) => (
              <AppointmentCard
                key={appt.SR_NO || Math.random()}
                appt={appt}
                onCancel={(a) => setCancelModal(a)}
                onReschedule={(a) => setRescheduleModal(a)}
              />
            ))}
            <Pagination page={page} totalPages={totalPages} onPage={(p) => setPage(p)} />
          </>
        )}
      </div>

      {/* Cancel modal */}
      {cancelModal && (
        <CancelModal appointment={cancelModal} onClose={() => setCancelModal(null)} onConfirm={handleCancelConfirm} loading={cancelLoading} />
      )}

      {/* Reschedule modal */}
      {rescheduleModal && (
        <RescheduleModal
          appointment={rescheduleModal}
          patient={patient}
          onClose={() => setRescheduleModal(null)}
          onSuccess={handleRescheduleSuccess}
        />
      )}
    </main>
  );
}
